import React, { useRef } from "react"
import { ItemGroup, ItemGroupSummary, OrderTracker, OrderTrackerStep, PurchaseHistory } from "../component";
import TrackerSteps, { TrackerStep, TrackerStepStatus } from "./TrackerSteps";
import HeavyDivider from "../common/HeavyDivider";
import GenerateOrderSummaryItems from "../common/GenerateOrderSummaryItems";
import DeliveryTracker, { PrimaryActionStyle } from "../delivery/DeliveryTracker";
import DeliveryPhotoComponent from "./DeliveryPhotoComponent";
import { TrackerPageBreadCrumb } from "../common/BreadCrumb";
import { AboveTheFoldMarker, CriticalFeatureMarker, FunctionalMarker } from "../csa/markers";
import ReportDamagedOrMissingMedicationsBottomSheet from "./ReportDamagedOrMissingMedicationsBottomSheet";
import { generateCsaTagSuffix } from "../csa/utils";
import DigitalShiplitLink from "../order/DigitalShiplitLink";
import DigitalShiplitBottomSheet from "../order/DigitalShiplitBottomSheet";
import findOrderKeyIdAndCheckShiplitStatus from "./DigitalShiplitTrackerPageHelper"
import { DigitalShiplitPageContext } from "../component.d";
interface TrackerPageProps {
    orderTracker: OrderTracker;
    itemGroupSummary: ItemGroupSummary;
    purchaseHistory: PurchaseHistory;
}
function mapEventUpdateLine(trackerSteps: OrderTrackerStep[]): TrackerStep[] {
    const mapStatus = (status: string) => {
        switch (status) {
            case "COMPLETE": {
                return TrackerStepStatus.COMPLETE;
            }
            case "IN_PROGRESS": {
                return TrackerStepStatus.IN_PROGRESS;
            }
            case "PENDING": {
                return TrackerStepStatus.PENDING;
            }
            default: {
                return TrackerStepStatus.PENDING
            }
        }
    }
    return trackerSteps.map(
        step => {
            return { label: step.label, status: mapStatus(step.status), eventUpdateLine: step.completionDate }
        }
    );
}
function TrackerPage(props: TrackerPageProps) {
    const bottomSheetRef = useRef<any>(null);
    const handleShiplitClick = (orderKeyId: string) => {
        bottomSheetRef.current?.showBottomSheet(orderKeyId);
    };
    const generateAllUpdatesLink = () => {
        if (!props.orderTracker.delivery || props.orderTracker.delivery.status.status === "NotShipped" ||
            props.orderTracker.delivery.status.status === "None") {
            return <span style={{ marginBottom: "20px" }} />;
        } else {
            return <span style={{ marginBottom: "20px" }}>
                <DeliveryTracker headline={props.itemGroupSummary.headline}
                    label={props.orderTracker.delivery?.label || props.itemGroupSummary.note}
                    delivery={props.orderTracker.delivery}
                    primaryActionStyle={PrimaryActionStyle.LINK}
                    primaryActionLabel="See all updates" />
            </span>
        }
    }
    const generateDeliveryPhotoInformation = () => {
        if (props.orderTracker.delivery?.status.status !== "Delivered") {
            return <></>;
        } else {
            return <>
                {props.orderTracker.delivery.deliveryPhoto != null && <DeliveryPhotoComponent deliveryPhoto={props.orderTracker.delivery.deliveryPhoto} width="288px" height="384px" />}
            </>;
        }
    }
    const generateReportDamagedOrMissingMedications = () => {
        if (props.orderTracker.delivery?.status.status !== "Delivered") {
            return null;
        } else {
            return <ReportDamagedOrMissingMedicationsBottomSheet
                reportDamagedOrMissingMedications={props.orderTracker.labels.reportDamagedOrMissingMedications} />
        }
    }
    const generateCarrierInformation = () => {
        return props.orderTracker.delivery &&
            <>
                <pui-text input={`Carrier: ${props.orderTracker.delivery.carrier}`} />
                <pui-text input={`Tracking ID: ${props.orderTracker.delivery.trackingNumber}`} />
                {!props.orderTracker.isPillPackComDelivery && generateAllUpdatesLink()}
            </>;
    }
    const hasBottleOrPackets = () => {
        const bottleMedsLength = props.itemGroupSummary?.bottleGroupSummary?.itemGroups?.length;
        const pillPackMedsLength = props.itemGroupSummary?.pillPackGroupSummary?.itemGroups?.length;
        return bottleMedsLength > 0 || pillPackMedsLength > 0
    }
    return <>
        <pui-stack id={`tracker-page${generateCsaTagSuffix(hasBottleOrPackets())}`}
            data-csa-c-content-id={`tracker-page${generateCsaTagSuffix(hasBottleOrPackets())}`}
            data-csa-c-type="card"
            data-csa-c-slot-id="tracker">
            <TrackerPageBreadCrumb
                yourOrders={props.purchaseHistory.labels.yourOrders}
                tracking={props.orderTracker.labels.tracking}
            />
            <pui-heading input={props.orderTracker.labels.tracking} headingLevel="1" textSize="large"
                spacingBottom="medium" />
            <pui-heading input={props.itemGroupSummary?.headline} headingLevel="2" spacingBottom="medium" />
            <pui-heading input={props.orderTracker.headline} headingLevel="3" textSize="small"
                spacingBottom={props.orderTracker.subline ? "mini" : "medium"} />
            {props.orderTracker.subline &&
                <pui-text input={props.orderTracker.subline} spacingBottom="medium" />}
            {generateDeliveryPhotoInformation()}
            <TrackerSteps steps={mapEventUpdateLine(props.orderTracker.trackerSteps)} />
            <AboveTheFoldMarker />
            <CriticalFeatureMarker />
            <pui-text input={props.orderTracker.labels.timeAlert} textSize="small" spacingTop="medium" />
        </pui-stack>
        {generateReportDamagedOrMissingMedications()}
        <HeavyDivider />
        <pui-heading input={props.orderTracker.labels.orderSummary} headingLevel="2" textSize="medium"
            spacingBottom="small" />
        <GenerateOrderSummaryItems
            itemGroupSummary={props.itemGroupSummary}
            isPillPackComDelivery={props.orderTracker.isPillPackComDelivery}
        />
        {(findOrderKeyIdAndCheckShiplitStatus(props.itemGroupSummary) !== undefined) &&
            (
                <DigitalShiplitLink
                    orderKeyId={findOrderKeyIdAndCheckShiplitStatus(props.itemGroupSummary)}
                    showShiplit={true}
                    labels={{
                        viewShipmentLiterature: props.orderTracker.labels.viewShipmentLiterature,
                        downloadingShipmentLiterature: props.orderTracker.labels.downloadingShipmentLiterature,
                        downloadingErrorShipmentLiterature: props.orderTracker.labels.downloadingErrorShipmentLiterature,
                        medicationInformationShipmentLiterature: props.orderTracker.labels.medicationInformationShipmentLiterature,
                        downloadingDoneShipmentLiterature: props.orderTracker.labels.downloadingDoneShipmentLiterature,
                        continueShipmentLiterature: props.orderTracker.labels.continueShipmentLiterature,
                    }}
                    isIosDevice={props.purchaseHistory.iosDevice}
                    isAndroidDevice={props.purchaseHistory.androidDevice}
                    isMShop={props.purchaseHistory.mShop}
                    onShiplitClick={handleShiplitClick}
                />
            )}
        {props.itemGroupSummary.address.lines.length > 0 &&
            <pui-stack>
                <HeavyDivider />
                <pui-heading input={props.orderTracker.labels.deliveryInformation} text="large" spacingBottom="medium" />
                {generateCarrierInformation()}
                {props.orderTracker.isPillPackComDelivery && <pui-heading></pui-heading>}
                <pui-text input={props.purchaseHistory.labels.deliveryAddress} fontweight="bold" />
                <pui-stack direction="horizontal" secondaryAxisArrangement="center">
                    <pui-stack direction="vertical" width="100%">
                        {props.itemGroupSummary.address.lines.map(value => <pui-text key={value} input={value} />)}
                    </pui-stack>
                </pui-stack>
            </pui-stack>}
        <FunctionalMarker />
        {/* Digital Shiplit Bottom Sheet */}
        <DigitalShiplitBottomSheet
            bottomSheetRef={bottomSheetRef}
            labels={{
                viewShipmentLiterature: props.orderTracker.labels.viewShipmentLiterature,
                downloadingShipmentLiterature: props.orderTracker.labels.downloadingShipmentLiterature,
                downloadingErrorShipmentLiterature: props.orderTracker.labels.downloadingErrorShipmentLiterature,
                medicationInformationShipmentLiterature: props.orderTracker.labels.medicationInformationShipmentLiterature,
                downloadingDoneShipmentLiterature: props.orderTracker.labels.downloadingDoneShipmentLiterature,
                continueShipmentLiterature: props.orderTracker.labels.continueShipmentLiterature,
                pdfNameShipmentLiterature: props.orderTracker.labels.pdfNameShipmentLiterature
            }}
            isIosDevice={props.purchaseHistory.iosDevice}
            isAndroidDevice={props.purchaseHistory.androidDevice}
            isMShop={props.purchaseHistory.mShop}
            currentPage={DigitalShiplitPageContext.OrderTracker}
        />
    </>
}
export default TrackerPage;